import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Activity, Stethoscope, TrendingUp, BookOpen, FileText } from 'lucide-react';

export const FrozenShoulder = () => {
  const faqs = [
    {
      question: 'What causes frozen shoulder?',
      answer: 'The exact cause remains unclear, but research suggests it involves inflammation and fibrosis of the shoulder capsule. Risk factors include diabetes mellitus (10-36% of diabetics develop frozen shoulder), thyroid disorders, prolonged immobility, and certain systemic conditions. Women aged 40-60 are at higher risk. Studies published in the Journal of Bone and Joint Surgery confirm these associations.'
    },
    {
      question: 'How long does frozen shoulder last?',
      answer: 'According to systematic reviews published in the British Journal of Sports Medicine, frozen shoulder typically progresses through three stages over 12-30 months: freezing (increasing pain and stiffness, 2-9 months), frozen (pain may decrease but stiffness remains, 4-12 months), and thawing (gradual improvement, 6-24 months). However, research shows that 20-50% of patients may have residual symptoms beyond 3 years.'
    },
    {
      question: 'Can frozen shoulder be prevented?',
      answer: 'Evidence suggests maintaining early shoulder mobility after injuries or surgery can reduce risk. The AAOS guidelines recommend gentle range-of-motion exercises post-injury. Optimizing glycemic control in diabetics is particularly important, as poorly controlled diabetes significantly increases risk.'
    },
    {
      question: 'What is the most effective treatment?',
      answer: 'According to the 2020 American Academy of Orthopaedic Surgeons (AAOS) guidelines and systematic reviews, a combination of physiotherapy focused on stretching and corticosteroid injections shows the best results in early stages. Hydrodilatation has moderate evidence. Arthroscopic capsular release is reserved for refractory cases beyond 6-12 months of conservative treatment.'
    },
    {
      question: 'Can frozen shoulder come back?',
      answer: 'Research published in the Journal of Shoulder and Elbow Surgery shows recurrence in the same shoulder is rare (less than 5%). However, approximately 6-17% of people may develop frozen shoulder in the opposite shoulder within 5 years. Diabetic patients have higher recurrence rates.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frozen Shoulder Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600">
              Stages, exercises, injections & surgery options by Dr. Deepthi Nandan Reddy at Apollo Hospitals
            </p>
          </div>
        </div>
      </section>

      {/* What is it */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What is Frozen Shoulder?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Frozen shoulder, medically known as adhesive capsulitis, is a condition characterized by progressive stiffness and pain in the shoulder joint. The shoulder capsule (the connective tissue surrounding the joint) becomes thick, tight, and inflamed, severely limiting movement.
              </p>
              <p>
                The condition develops gradually and typically affects people between 40 and 60 years of age. It is more common in women and people with certain medical conditions, particularly diabetes, thyroid disorders, and cardiovascular disease.
              </p>
              <p>
                While frozen shoulder can be frustrating and limit daily activities, most people eventually recover with appropriate treatment, although the process can take months to years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stages */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Three Stages of Frozen Shoulder
            </h2>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-red-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-red-500">Stage 1</span>
                    Freezing (Painful Stage)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p><strong>Duration:</strong> 2-9 months</p>
                  <p><strong>Symptoms:</strong> Gradual onset of pain that worsens over time, especially at night. Progressive loss of shoulder movement.</p>
                  <p><strong>Characteristics:</strong> Pain is the dominant feature. Movement becomes increasingly limited.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-orange-500">Stage 2</span>
                    Frozen (Stiff Stage)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p><strong>Duration:</strong> 4-12 months</p>
                  <p><strong>Symptoms:</strong> Pain may decrease, but stiffness remains severe or worsens. Daily activities become very difficult.</p>
                  <p><strong>Characteristics:</strong> Shoulder movement is severely restricted in all directions.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-green-500">Stage 3</span>
                    Thawing (Recovery Stage)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p><strong>Duration:</strong> 6-24 months</p>
                  <p><strong>Symptoms:</strong> Gradual improvement in range of motion. Pain continues to decrease.</p>
                  <p><strong>Characteristics:</strong> Shoulder function slowly returns, though some limitations may persist.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Causes & Risk Factors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Causes and Risk Factors
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Medical Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Diabetes (10-20% of diabetics develop frozen shoulder)</p>
                  <p>• Thyroid disorders (hyper or hypothyroidism)</p>
                  <p>• Cardiovascular disease</p>
                  <p>• Parkinson&apos;s disease</p>
                  <p>• Previous stroke</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Other Risk Factors
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Age 40-60 years</p>
                  <p>• Female gender</p>
                  <p>• Recent shoulder injury or surgery</p>
                  <p>• Prolonged immobilization of the arm</p>
                  <p>• Previous history in the opposite shoulder</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Symptoms
              </h2>
            </div>
            <div className="bg-orange-50 p-6 rounded-lg space-y-3">
              <p className="text-gray-700">• Progressive shoulder pain, especially at night</p>
              <p className="text-gray-700">• Severe restriction in shoulder movement (passive and active)</p>
              <p className="text-gray-700">• Difficulty performing everyday tasks (dressing, reaching overhead, reaching behind back)</p>
              <p className="text-gray-700">• Pain when lying on the affected shoulder</p>
              <p className="text-gray-700">• Compensatory movements using other muscles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnosis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Diagnosis
              </h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Diagnosis is primarily clinical, based on history and physical examination:
              </p>
              <p>
                <strong>Physical Examination:</strong> Assessment of active and passive range of motion. In frozen shoulder, both are significantly limited, particularly external rotation and abduction.
              </p>
              <p>
                <strong>Imaging:</strong> X-rays are usually normal but help rule out other conditions like arthritis. MRI may be ordered if the diagnosis is unclear or to exclude other shoulder pathology.
              </p>
              <p>
                The key diagnostic feature is severe restriction in passive range of motion, distinguishing it from rotator cuff problems where passive motion is typically preserved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Evidence-Based Treatment */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Evidence-Based Treatment Options
              </h2>
            </div>
            
            {/* Evidence Note */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r">
              <div className="flex items-start gap-2">
                <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Treatment recommendations based on AAOS Clinical Practice Guidelines (2020), British Elbow & Shoulder Society (BESS) guidelines, and systematic reviews published in JBJS and BJSM.
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">STRONG EVIDENCE</span>
                    First-Line Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Physiotherapy (Grade A Recommendation):</strong></p>
                  <p>• Supervised stretching programs targeting capsular stiffness</p>
                  <p>• Low-load prolonged stretching shown most effective in randomized trials</p>
                  <p>• Focus on external rotation and abduction</p>
                  <p className="mt-3"><strong>Intra-articular Corticosteroid Injection (Grade A):</strong></p>
                  <p>• Most effective in the early &ldquo;freezing&rdquo; stage (first 3-6 months)</p>
                  <p>• Systematic reviews show significant short-term improvement in pain and ROM</p>
                  <p>• Image-guided injection improves accuracy and outcomes</p>
                  <p className="mt-3"><strong>Pain Management:</strong></p>
                  <p>• NSAIDs for symptomatic relief</p>
                  <p>• Heat therapy before stretching sessions</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-yellow-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">MODERATE EVIDENCE</span>
                    Second-Line Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Hydrodilatation (Distension Arthrography):</strong></p>
                  <p>• Injection of saline and steroid to distend the capsule</p>
                  <p>• Cochrane review shows short-term benefits for pain and function</p>
                  <p>• Consider when physiotherapy + injections provide insufficient relief</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">SURGICAL OPTIONS</span>
                    Refractory Cases (After 6-12 Months)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Arthroscopic Capsular Release (Preferred):</strong></p>
                  <p>• Minimally invasive release of contracted capsule and ligaments</p>
                  <p>• Gold standard surgical intervention for refractory frozen shoulder</p>
                  <p>• 85-90% success rate in peer-reviewed studies</p>
                  <p>• Allows immediate post-operative physiotherapy for best results</p>
                  <p>• Lower complication rate compared to open procedures</p>
                  <p className="text-sm text-gray-600 mt-3 bg-gray-100 p-3 rounded">
                    <strong>Note:</strong> Surgery is considered when 6-12 months of consistent conservative treatment fails to provide adequate relief, or when functional limitations significantly impact quality of life.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Recovery Expectations
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                <strong>Natural History:</strong> Long-term studies published in JBJS show that even without treatment, frozen shoulder typically resolves in 1-3 years. However, a 2020 systematic review found that 20-50% of patients may have residual stiffness or symptoms persisting beyond 3 years.
              </p>
              <p>
                <strong>With Evidence-Based Treatment:</strong> Structured physiotherapy combined with steroid injections can significantly accelerate recovery and improve outcomes. Cochrane reviews confirm that early intervention produces better results than watchful waiting.
              </p>
              <p>
                <strong>Post-Surgical Recovery (Arthroscopic Release):</strong> Immediate physiotherapy begins within 24 hours to prevent recurrence of stiffness. Published data shows 85-90% of patients achieve significant improvement within 3-6 months post-surgery.
              </p>
              <p>
                <strong>Prognosis:</strong> The majority of patients (90%) achieve full or near-full recovery with appropriate treatment. Diabetic patients and those with bilateral involvement may have a more prolonged course.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific References */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References & Guidelines</h3>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-sm text-gray-600 space-y-2">
              <p>1. AAOS Clinical Practice Guidelines for Adhesive Capsulitis (2020)</p>
              <p>2. Cochrane Database: &ldquo;Interventions for frozen shoulder&rdquo; (Buchbinder R, et al.)</p>
              <p>3. Journal of Bone &amp; Joint Surgery: &ldquo;Natural History of Frozen Shoulder&rdquo;</p>
              <p>4. British Journal of Sports Medicine: Systematic Reviews on Treatment Efficacy</p>
              <p>5. Journal of Shoulder and Elbow Surgery: Long-term Outcomes Studies</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">
                Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection />
    </div>
  );
};